import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { paymentsAPI } from './PaymentApi';

/**
 * The card provider's two halves. Stripe-specific, deliberately.
 *
 * Named for Stripe rather than for "checkout" because that is all this is: the
 * redirect out and the pick-up on return. Cash, Venmo and anything settled at
 * the desk never touch these — choosing a method lives in
 * the checkout page, and recording money taken in person is markOrderPaid on
 * the API. A file called usePaymentCheckout would send the next person looking
 * for counter handling here, and they would not find it.
 */

/**
 * Sending someone to Stripe.
 *
 * Deliberately separate from useStripeReturn below: the component that starts
 * a payment is often not the one that is on screen when the payer comes back.
 * Tournament registration is exactly that — checkout starts on the Register
 * tab and returns to Enrolled, and tabs here only mount once visited, so a
 * hook that did both would simply never run on the way back.
 */
export default function useStripeCheckout() {
  const [redirecting, setRedirecting] = useState(false);

  /**
   * Resolves only if something went wrong — on the happy path the browser has
   * already left for Stripe.
   */
  const startCheckout = useCallback(async ({ kind, registrationIds, returnPath, cancelPath }) => {
    setRedirecting(true);
    try {
      const { url, sessionId } = await paymentsAPI.createCheckoutSession({
        kind, registrationIds, returnPath, cancelPath,
      });
      if (!url) throw new Error('Stripe did not return a checkout URL');

      // Starting a payment ends the previous one's arrival. Without this a
      // payer who paid, then began a second checkout and pressed back, was
      // shown the FIRST payment's receipt — the record was still inside its
      // window and on the same path, so nothing else ruled it out. In the
      // native apps the webview stays alive behind Stripe, so that stale
      // record really does survive the trip.
      clearStripeReturn();

      // Remembered so the return can be resolved from the session itself
      // rather than from whatever the URL happens to say. Stripe's own back
      // link carries ?payment=cancelled, but the device back button carries
      // nothing at all — and that is the case that used to come back blank.
      // sessionStorage, not module state: on the web the redirect tears the
      // app down, and only this survives it.
      writePendingCheckout({ sessionId, kind, registrationIds, path: returnPath });

      window.location.assign(url);
      return { redirected: true };
    } catch (err) {
      setRedirecting(false);
      toast.error(err?.message || 'Could not start card payment');
      return { redirected: false, error: err };
    }
  }, []);

  return { startCheckout, redirecting };
}

/**
 * One verification per Stripe session, shared by every copy of useStripeReturn.
 *
 * More than one copy can be mounted on a real page — a page and a tab inside it
 * both picking up the same return — and they all read the same ?session_id. Left to themselves they each fired their
 * own request, and the first to answer stripped the params, which are the
 * effect's own dependencies: every other copy tore down mid-flight and dropped
 * its result and its onPaid on the floor. The payer was charged and the screen
 * that owed them a receipt never heard about it.
 *
 * Keyed by session id and kept after the URL is cleared, because a copy that
 * mounts late (a tab that only renders once its data arrives) still has to be
 * able to learn the outcome — by then there is nothing left in the URL to read.
 */
const stripeReturns = new Map();

/**
 * The session the browser came back with, remembered past the URL cleanup —
 * but only for as long as the arrival lasts.
 *
 * The window matters. Without one this record lived for the whole page session,
 * so every later mount of the hook re-derived "paid" from it: the confirmation
 * came back after being dismissed, on tabs the payer had since navigated to,
 * and would not go away short of a full reload. A receipt is for the arrival it
 * belongs to, not for the rest of the visit.
 *
 * Thirty seconds is sized for the thing it exists to solve: a tab that renders
 * once its data has loaded, a second or two behind the page. Nothing legitimate
 * mounts half a minute later still expecting to be told.
 */
const RETURN_MEMORY_MS = 30_000;
let lastStripeReturn = null;

/**
 * Set while a screen is reopening checkout for an unfinished payment, so the
 * page-level banner does not repeat what the dialog already says.
 *
 * Listeners rather than a bare flag because the two hooks cannot be ordered.
 * The banner lives on the page and the dialog inside a tab that only mounts
 * once the academy has loaded, so the page had already drawn "Payment
 * cancelled" before the tab existed to claim it. The claim therefore has to
 * reach back and retract it.
 */
let retryIsPresenting = false;
const retryClaimListeners = new Set();

function claimRetryPresentation() {
  retryIsPresenting = true;
  retryClaimListeners.forEach((fn) => fn());
}

function rememberedReturn(currentPath) {
  if (!lastStripeReturn) return null;
  if (Date.now() - lastStripeReturn.at > RETURN_MEMORY_MS) {
    lastStripeReturn = null;
    return null;
  }
  // Only for the screen the payer actually came back to. Sibling copies of the
  // hook mount on that same screen a beat apart, which is what this exists for;
  // a different path means the payer moved, and a move ends the arrival.
  if (lastStripeReturn.path && lastStripeReturn.path !== currentPath) {
    lastStripeReturn = null;
    return null;
  }
  return lastStripeReturn;
}

export function noteStripeReturn(sessionId, payment, currentPath) {
  if (!sessionId) return;
  if (lastStripeReturn?.sessionId === sessionId) return;   // keep the original timestamp
  // The path is part of the record. The window alone was not enough: a payer
  // who paid and then pressed back within it was shown the confirmation again,
  // on a screen they had navigated to rather than arrived at. Tying it to where
  // it happened means a receipt can only reappear where it was earned.
  lastStripeReturn = { sessionId, payment, at: Date.now(), path: currentPath ?? null };
}

/** Ends the current arrival. A new payment invalidates the previous receipt. */
export function clearStripeReturn() {
  lastStripeReturn = null;
}

/**
 * The checkout that is out at Stripe right now.
 *
 * Survives the redirect because it lives in sessionStorage — on the web the
 * whole app is torn down and rebuilt, so anything in module state is gone by
 * the time the payer comes back. Scoped to the tab, and cleared as soon as the
 * outcome has been shown, so it can never be mistaken for a later visit.
 *
 * Storage is wrapped because Safari throws on write in private browsing rather
 * than degrading, and a payment must not fail for want of a note to self.
 */
const PENDING_KEY = 'kynaix.stripe.pendingCheckout';

export function writePendingCheckout(pending) {
  if (!pending?.sessionId) return;
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...pending, at: Date.now() }));
  } catch { /* private mode — the return falls back to reading the URL */ }
}

export function readPendingCheckout() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearPendingCheckout() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* nothing to clear */ }
}

export function verifyStripeReturn(sessionId, fetcher) {
  if (!stripeReturns.has(sessionId)) {
    stripeReturns.set(sessionId, fetcher(sessionId));
  }
  return stripeReturns.get(sessionId);
}

/** Test seam — module state outlives a single render tree. */
export function resetStripeReturns() {
  stripeReturns.clear();
  lastStripeReturn = null;
  retryIsPresenting = false;
  retryClaimListeners.clear();
  clearPendingCheckout();
}


/**
 * Picking the payer back up when Stripe returns them.
 *
 * Mount this on the page, not inside a tab or step that may not be rendered
 * when the redirect lands.
 *
 * "?payment=success" is not trusted — anyone can type it. The session id
 * beside it is re-read from Stripe by the server, which is what actually
 * decides whether anything gets marked paid.
 *
 * @returns {{result: {state: 'paid'|'pending'|'failed'|'cancelled', message: string, amount?: number}|null,
 *            verifying: boolean, dismiss: () => void}}
 */
export function useStripeReturn({ onPaid } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  // The router's path, not window.location — the rest of this hook reads the
  // router, and under a MemoryRouter the two disagree.
  const { pathname } = useLocation();
  const [result, setResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Verification starts on mount and finishes a second or so later, by which
  // time the page has loaded its own data and rebuilt its callbacks. Holding
  // onPaid in a ref means we call the current one rather than the version
  // captured at mount — that one closed over a half-loaded page, so its
  // "reload the participants" call quietly bailed out and the list kept
  // showing a payment that had in fact gone through.
  const onPaidRef = useRef(onPaid);
  useEffect(() => { onPaidRef.current = onPaid; });

  // Retract a "cancelled" notice if a screen reopens checkout for it. Only
  // that state — a receipt is this hook's to show, and must not be taken away.
  useEffect(() => {
    const retract = () => setResult((r) => (r?.state === 'cancelled' ? null : r));
    retryClaimListeners.add(retract);
    return () => { retryClaimListeners.delete(retract); };
  }, []);

  // Read from the URL when it is still there, and from the shared record once
  // some other copy has cleared it. Without the fallback, a copy that mounts a
  // beat later finds a clean URL and concludes nothing happened.
  const urlSessionId = searchParams.get('session_id');
  const urlPayment = searchParams.get('payment');
  if (urlSessionId && urlPayment) noteStripeReturn(urlSessionId, urlPayment, pathname);

  // An explicit cancel ends the arrival outright, and must not fall back to
  // anything remembered. It used to: the effect set "cancelled", stripped the
  // params, and the very next render — now with a clean URL — read the previous
  // payment's record instead, re-verified that old session and flipped the
  // screen to "paid". Coming back without paying announced a receipt.
  const cancelledInUrl = urlPayment === 'cancelled';
  if (cancelledInUrl) clearStripeReturn();

  const remembered = (urlSessionId || cancelledInUrl) ? null : rememberedReturn(pathname);
  const sessionId = urlSessionId || remembered?.sessionId || null;
  const paymentParam = urlPayment || remembered?.payment || null;

  useEffect(() => {
    if (!paymentParam) return;

    // Strip the Stripe params straight away. They have served their purpose,
    // and leaving them means a refresh re-runs all of this.
    const clear = () => setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('payment');
      next.delete('session_id');
      return next;
    }, { replace: true });

    if (paymentParam === 'cancelled') {
      // A screen that reopens checkout says all of this, and better — it offers
      // another way to pay rather than only reporting the bad news. Saying it
      // here as well put the same sentence on the screen twice, once in the
      // dialog and once in a bar beneath it.
      if (retryIsPresenting) { clear(); return; }
      setResult({
        state: 'cancelled',
        message: 'Payment cancelled. Your place is held but still unpaid.',
      });
      clear();
      return;
    }

    if (paymentParam !== 'success' || !sessionId) return;

    let cancelled = false;
    setVerifying(true);
    // Shared, so N mounted copies make one request and all N are told the
    // answer — including the ones whose params were cleared out from under them.
    verifyStripeReturn(sessionId, (id) => paymentsAPI.getCheckoutStatus(id))
      .then((res) => {
        if (cancelled) return;
        if (res.paid) {
          setResult({
            state: 'paid',
            amount: res.amount,
            message: 'Payment received. Your registration is confirmed.',
          });
          onPaidRef.current?.(res);
        } else {
          setResult({
            state: 'pending',
            message: `Payment is ${res.paymentStatus || 'still processing'}. This will update once it clears.`,
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // Whatever went wrong here is ours or Stripe's, not something the
        // payer can act on — "Resource not found" tells them nothing and
        // frightens someone who has just been charged. Say what matters:
        // whether money may have left their account, and what to do next.
        console.error('Stripe session verification failed:', err);
        setResult({
          state: 'failed',
          message: "We couldn't confirm this payment. If your card was charged, "
                 + 'nothing is lost — contact the organizer and they can check.',
        });
      })
      // `clear()` strips ?payment&session_id, which used to change this effect's
      // own dependencies: the effect tore itself down, set `cancelled`, and the
      // in-flight promise then dropped its result. On a page mounting this hook
      // twice — and the Programs tab is one — whichever copy cleared the params
      // first stranded the other, so a payer came back from Stripe charged and
      // the registration screen never showed the receipt it owed them.
      //
      // `sessionId` and `paymentParam` now fall back to the module-level record,
      // so clearing the URL leaves the dependencies unchanged, nothing tears
      // down, and every copy sees the shared answer. `cancelled` is left in
      // place for the case it was always meant for: a real unmount.
      .finally(() => { setVerifying(false); if (!cancelled) clear(); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentParam, sessionId]);

  return { result, verifying, dismiss: () => setResult(null) };
}
